import React from 'react';
import { Row, Col, Card, Statistic, Progress, Badge, Spin, Alert } from 'antd';
import {
  CheckCircleOutlined,
  SyncOutlined,
  ClockCircleOutlined,
  ExperimentOutlined,
  RiseOutlined,
  DashboardOutlined,
} from '@ant-design/icons';
import { useProductionOverview } from '../../../hooks/useManufacturingDashboard';

interface RealTimeStatusSectionProps {
  date?: string;
  refreshInterval?: number;
}

const RealTimeStatusSection: React.FC<RealTimeStatusSectionProps> = ({
  date,
  refreshInterval,
}) => {
  const { data: overview, isLoading, error } = useProductionOverview(date, refreshInterval);

  if (error) {
    return (
      <Alert
        message="Error Loading Production Overview"
        description={error.message}
        type="error"
        showIcon
        style={{ marginBottom: 24 }}
      />
    );
  }

  if (isLoading || !overview) {
    return (
      <Card style={{ marginBottom: 24 }}>
        <div style={{ textAlign: 'center', padding: '40px 0' }}>
          <Spin size="large" />
        </div>
      </Card>
    );
  }

  const statusColors: Record<string, string> = {
    'Draft': '#d9d9d9',
    'Released': '#145A54',
    'In Progress': '#52c41a',
    'Completed': '#389e0d',
    'Inspection Pending': '#faad14',
    'Inspection Rejected': '#cf1322',
    'Closed': '#8c8c8c',
    'Cancelled': '#ff4d4f',
  };

  // Calculate total for percentages
  const totalDistribution = overview.statusDistribution.reduce((sum, s) => sum + s.count, 0);

  return (
    <div style={{ marginBottom: 24 }}>
      {/* Main Status Cards */}
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Total Work Orders"
              value={overview.totalWorkOrders}
              prefix={<DashboardOutlined />}
              valueStyle={{ color: '#145A54' }}
            />
            <Badge
              status="processing"
              text="Live"
              style={{ marginTop: 8 }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Active Work Orders"
              value={overview.activeWorkOrders}
              prefix={<SyncOutlined spin />}
              valueStyle={{ color: '#52c41a' }}
            />
            <div style={{ marginTop: 8, fontSize: 12, color: '#8c8c8c' }}>
              Currently in progress
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Completed Today"
              value={overview.completedToday}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: '#389e0d' }}
            />
            <div style={{ marginTop: 8, fontSize: 12, color: '#8c8c8c' }}>
              Production rate: {overview.productionRate.toFixed(1)} units/hr
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Pending Inspection"
              value={overview.pendingInspection}
              prefix={<ExperimentOutlined />}
              valueStyle={{ color: '#faad14' }}
            />
            <div style={{ marginTop: 8, fontSize: 12, color: '#8c8c8c' }}>
              Awaiting quality check
            </div>
          </Card>
        </Col>
      </Row>

      {/* Status Distribution & Metrics */}
      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col xs={24} lg={18}>
          <Card
            title="Status Distribution"
            extra={<Badge status="processing" text="Real-time" />}
          >
            <div style={{ marginBottom: 16 }}>
              {overview.statusDistribution.map((status) => {
                const percentage = totalDistribution > 0
                  ? (status.count / totalDistribution) * 100
                  : 0;

                return (
                  <div key={status.status} style={{ marginBottom: 8 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                      <span style={{ fontSize: 14 }}>
                        {status.status}
                      </span>
                      <span style={{ fontSize: 14, fontWeight: 500 }}>
                        {status.count} ({percentage.toFixed(1)}%)
                      </span>
                    </div>
                    <Progress
                      percent={percentage}
                      strokeColor={statusColors[status.status] || '#145A54'}
                      showInfo={false}
                      size="small"
                    />
                  </div>
                );
              })}
            </div>
          </Card>
        </Col>
        <Col xs={24} lg={6}>
          <Card
            title="Overall Metrics"
            style={{ height: '100%' }}
          >
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 12, color: '#8c8c8c', marginBottom: 4 }}>
                Production Rate
              </div>
              <div style={{ fontSize: 24, fontWeight: 500, color: '#145A54' }}>
                <RiseOutlined /> {overview.productionRate.toFixed(1)}
              </div>
              <div style={{ fontSize: 12, color: '#8c8c8c' }}>
                units per hour
              </div>
            </div>
            <div>
              <div style={{ fontSize: 12, color: '#8c8c8c', marginBottom: 4 }}>
                Utilization Rate
              </div>
              <Progress
                type="circle"
                percent={overview.utilizationRate}
                strokeColor={{
                  '0%': overview.utilizationRate > 85 ? '#52c41a' : '#145A54',
                  '100%': overview.utilizationRate > 85 ? '#389e0d' : '#096dd9',
                }}
                width={80}
              />
            </div>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default RealTimeStatusSection;
